/**
 * A loop join that does not click.
 *
 * A buffer source turns around at `loopEnd` and lands on `loopStart`, and
 * unless the waveform happens to match at both, the join is a step — a click,
 * once per pass. The remedy is the same as for any two pieces of audio that
 * meet: crossfade them. The last `crossfade` seconds before the loop's end are
 * blended with the same length of audio leading into the loop's start, so by
 * the time the source reaches the end it is playing exactly what precedes the
 * start, and the jump lands on a continuation of itself.
 *
 * A buffer source cannot do this on its own, so the blend is written into a
 * copy of the file and the copy is what plays. Only the samples inside the
 * fade change; the attack before the loop and the tail after it are untouched.
 */

/** The crossfade a zone can actually have: no longer than what leads into the loop, and no more than half the loop. */
export function usableCrossfade(loopStartSec: number, loopEndSec: number, wantedSec: number): number {
  const loopLength = loopEndSec - loopStartSec;
  return Math.max(0, Math.min(wantedSec, loopStartSec, loopLength / 2));
}

/**
 * Writes an equal-power crossfade into `out`, a copy of `source`, over the
 * `fadeFrames` before `loopEndFrame`, blending towards the audio before
 * `loopStartFrame`. Returns the number of frames actually faded.
 */
export function writeLoopCrossfade(
  channel: Float32Array,
  loopStartFrame: number,
  loopEndFrame: number,
  fadeFrames: number,
): number {
  const fade = Math.min(fadeFrames, loopStartFrame, Math.floor((loopEndFrame - loopStartFrame) / 2));
  if (fade <= 0) return 0;
  const endFrom = loopEndFrame - fade;
  const startFrom = loopStartFrame - fade;
  for (let i = 0; i < fade; i++) {
    // Equal power: the two are the same material a loop apart, correlated
    // enough that a linear blend would dip in the middle.
    const t = (i + 1) / (fade + 1);
    const angle = (t * Math.PI) / 2;
    channel[endFrom + i] = channel[endFrom + i] * Math.cos(angle) + channel[startFrom + i] * Math.sin(angle);
  }
  return fade;
}

export function crossfadedLoopCopy(
  ctx: BaseAudioContext,
  source: AudioBuffer,
  loopStartSec: number,
  loopEndSec: number,
  crossfadeSec: number,
): AudioBuffer {
  const copy = ctx.createBuffer(source.numberOfChannels, source.length, source.sampleRate);
  const rate = source.sampleRate;
  const loopStartFrame = Math.round(loopStartSec * rate);
  const loopEndFrame = Math.min(source.length, Math.round(loopEndSec * rate));
  const fadeFrames = Math.round(crossfadeSec * rate);
  for (let c = 0; c < source.numberOfChannels; c++) {
    const data = new Float32Array(source.length);
    source.copyFromChannel(data, c);
    writeLoopCrossfade(data, loopStartFrame, loopEndFrame, fadeFrames);
    copy.copyToChannel(data, c);
  }
  return copy;
}
